import { Box, Flex } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { MdOutlineEdit } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { ContextMenu } from "@nens/chakra/components";
import {
  MenuAction,
  RanaButton,
  RanaContextMenu,
  RanaEditable,
  RanaEditableHandle,
  RanaModal,
  TrashIcon,
} from "@nens/ranalib";

import { getMaps } from "../../store/selectors";
import { getIsEditable } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { PublicationMap } from "../../types/publication";

import { useMoveActions } from "./hooks/use-move-actions";

export function PublicationLeftMapList() {
  const openMap = usePublicationStore((state) => state.openMap);
  const maps = usePublicationStore((state) => state.maps);

  const [deleteTarget, setDeleteTarget] = useState<PublicationMap | null>(null);

  return (
    <Flex flexDirection="column" borderBottomWidth="base" borderColor="brand.glacier" pb={3}>
      {deleteTarget && <DeleteMapDialog map={deleteTarget} close={() => setDeleteTarget(null)} />}
      {maps.map((map, index) => (
        <MapItem
          map={map}
          isActive={map.id === openMap}
          key={map.id}
          isFirst={index === 0}
          isLast={index === maps.length - 1}
          deleteMap={setDeleteTarget}
        />
      ))}
    </Flex>
  );
}

function MapItem({
  map,
  isActive,
  isFirst,
  isLast,
  deleteMap,
}: {
  map: PublicationMap;
  isActive: boolean;
  isFirst: boolean;
  isLast: boolean;
  deleteMap: (map: PublicationMap) => void;
}) {
  const intl = useIntl();
  const numMaps = usePublicationStore(getMaps).length;
  const isEditable = usePublicationStore(getIsEditable);
  const setOpenMap = usePublicationStore((state) => state.setOpenMap);
  const moveMap = usePublicationStore((state) => state.moveMap);
  const setMapName = usePublicationStore((state) => state.setMapName);

  const editRef = useRef<RanaEditableHandle>(null);

  const moveActions = useMoveActions({
    onMove: (dir) => moveMap(map.id, dir),
    isFirst,
    isLast,
  });

  const actions: MenuAction[] = isEditable
    ? [
        {
          label: intl.formatMessage({
            id: "publication.map.contextmenu.rename_map",
            defaultMessage: "Rename map",
          }),
          icon: <MdOutlineEdit />,
          action: () => editRef.current?.startEditing(),
        },
        moveActions,
        {
          label: intl.formatMessage({
            id: "publication.map.contextmenu.remove_map",
            defaultMessage: "Remove map",
          }),
          icon: <TrashIcon />,
          action: () => deleteMap(map),
          disabledText:
            numMaps > 1
              ? undefined
              : intl.formatMessage({
                  id: "publication.map.contextmenu.remove_map.disabled",
                  defaultMessage: "A publication must have at least one map",
                }),
        },
      ]
    : [];

  return (
    <ContextMenu<HTMLDivElement>
      renderMenu={actions?.length ? () => <RanaContextMenu.Actions actions={actions} /> : null}
      onOpen={() => setOpenMap(map.id)}
    >
      {(ref) => (
        <Flex
          ref={ref}
          data-map-id={map.id}
          flexDirection="row"
          alignItems="center"
          gap={2}
          p={2}
          pl={4}
          bg={isActive ? "brand.selected" : "transparent"}
          borderWidth="base"
          borderStyle="dashed"
          borderColor="transparent"
          cursor="pointer"
          role="group"
          _hover={{ bg: "button.tertiary.hover" }}
          onClick={() => setOpenMap(map.id)}
        >
          <Box flexGrow={1}>
            <RanaEditable
              key={map.name}
              defaultValue={map.name}
              onSubmit={(name) => setMapName(map.id, name)}
            >
              <RanaEditable.Preview overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" />
              <RanaEditable.Input />
              <RanaEditable.EditHandle ref={editRef} />
            </RanaEditable>
          </Box>
          {isEditable && (
            <Box px={1} visibility="hidden" _groupHover={{ visibility: "visible" }}>
              <RanaContextMenu actions={actions} />
            </Box>
          )}
        </Flex>
      )}
    </ContextMenu>
  );
}

function DeleteMapDialog({ map, close }: { map: PublicationMap; close: () => void }) {
  const intl = useIntl();
  const deleteMap = usePublicationStore((state) => state.deleteMap);

  return (
    <RanaModal
      isOpen
      onClose={close}
      topBar={intl.formatMessage({
        id: "publication.map.deletemapmodal.title",
        defaultMessage: "Remove map",
      })}
      buttons={[
        <RanaButton key="cancel" variant="secondary" onClick={close}>
          <FormattedMessage id="publication.map.button.cancel" defaultMessage="Cancel" />
        </RanaButton>,
        <RanaButton
          key="delete"
          variant="primary"
          onClick={() => {
            deleteMap(map.id);
            close();
          }}
        >
          <FormattedMessage id="publication.map.button.remove_map" defaultMessage="Remove map" />
        </RanaButton>,
      ]}
    >
      <Box p={1}>
        <FormattedMessage
          id="publication.map.deletemapmodal.message"
          defaultMessage='Are you sure you want to remove the map "{name}"?'
          values={{ name: map.name }}
        />
      </Box>
    </RanaModal>
  );
}
